import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow, Typography, Box, useMediaQuery, useTheme, alpha } from '@mui/material';
import { getSlotItemsForHour, HOURS } from './cUtils';
import { CalendarItemChip } from './components/CalendarItemChip';
import { useTranslation } from 'react-i18next';
const styles = {
    headerCell: {
        bgcolor: '#fafbfc',
        borderBottom: '1px solid #eee',
        fontWeight: 500,
        color: '#8a8a8a',
        textAlign: 'center',
        background: '#fafbfc',
        padding: '8px 4px'
    },
    timeCell: {
        bgcolor: '#fafbfc',
        color: '#8a8a8a',
        border: '1px solid #f0f0f0',
        textAlign: 'center',
        fontSize: 14,
        fontWeight: 500,
        padding: '6px 2px'
    },
    eventCell: (isToday) => ({
        height: 48,
        verticalAlign: 'top',
        bgcolor: (theme) => (isToday ? alpha(theme.palette.primary.main, 0.08) : '#fff'),
        border: '1px solid #f0f0f0',
        textAlign: 'center',
        px: 1,
        py: 0.5
    })
};
const isDateToday = (date) => {
    const today = new Date();
    return (date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear());
};
const TimeCell = ({ hour, mobile }) => (_jsx(TableCell, { sx: {
        ...styles.timeCell,
        ...(mobile ? { width: '20%', maxWidth: 45 } : { width: 60, maxWidth: 60 })
    }, children: `${hour}:00` }));
const EventCell = ({ date, hour, items, handleOpenModal, mobile, isSponsorshipDetailsCalendarTab = false }) => {
    const isToday = isDateToday(date);
    const cellItems = getSlotItemsForHour(items, date, hour);
    return (_jsx(TableCell, { sx: {
            ...styles.eventCell(isToday),
            ...(mobile
                ? {
                    width: '80%',
                    px: 0.5,
                    py: 0.5
                }
                : {
                    minWidth: 100,
                    maxWidth: 130,
                    position: 'relative'
                })
        }, children: cellItems.map((item) => {
            return (_jsx(CalendarItemChip, { item: item, onClick: () => handleOpenModal?.(item), isSponsorshipDetailsCalendarTab: isSponsorshipDetailsCalendarTab }, item.id));
        }) }));
};
export const WeekView = ({ date, items, handleOpenModal, isSponsorshipDetailsCalendarTab = false }) => {
    const days = useMemo(() => {
        const start = new Date(date);
        const day = start.getDay();
        const mondayOffset = day === 0 ? -6 : 1 - day;
        start.setDate(date.getDate() + mondayOffset);
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            return d;
        });
    }, [date]);
    const [activeDayIndex, setActiveDayIndex] = useState(() => {
        const dateDay = date.getDay();
        const adjustedIndex = dateDay === 0 ? 6 : dateDay - 1;
        return adjustedIndex;
    });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const { t } = useTranslation(['form', 'validation', 'common']);
    const DaySelector = () => (_jsx(Box, { sx: { width: '100%' }, children: _jsx(Box, { sx: {
                display: 'flex',
                background: '#fafbfc',
                borderBottom: '1px solid #eee',
                p: 0,
                height: 61,
                m: 0,
                width: '100%'
            }, children: days.map((d, idx) => {
                const isActive = idx === activeDayIndex;
                const isToday = isDateToday(d);
                return (_jsxs(Box, { onClick: () => setActiveDayIndex(idx), sx: {
                        py: 0.5,
                        px: 0,
                        mx: 0,
                        flex: 1,
                        color: isToday
                            ? theme.palette.common.white
                            : isActive
                                ? theme.palette.primary.darker
                                : '#8a8a8a',
                        fontWeight: isActive ? 600 : 500,
                        bgcolor: isToday ? theme.palette.primary.darker : '#fafbfc',
                        borderBottom: isActive
                            ? `2px solid ${theme.palette.primary.darker}`
                            : '1px solid #eee',
                        textAlign: 'center',
                        cursor: 'pointer',
                        fontSize: '0.7rem',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 0
                    }, children: [_jsx(Typography, { variant: 'caption', noWrap: true, sx: { fontSize: '0.65rem', lineHeight: 1.1 }, children: d.toLocaleDateString(undefined, { weekday: 'short' }).slice(0, 3) }), _jsx(Typography, { variant: 'body2', component: 'span', sx: { fontSize: '0.75rem', lineHeight: 1.1 }, children: d.getDate() })] }, idx));
            }) }) }));
    const MobileView = () => {
        const activeDay = days[activeDayIndex];
        return (_jsxs(Box, { sx: { width: '100%' }, children: [_jsx(DaySelector, {}), _jsx(Box, { sx: { width: '100%' }, children: _jsxs(Table, { size: 'small', sx: {
                            width: '100%',
                            bgcolor: '#fff',
                            tableLayout: 'fixed',
                            borderSpacing: 0
                        }, children: [_jsx(TableHead, { children: _jsxs(TableRow, { style: { background: '#fafbfc' }, children: [_jsx(TableCell, { sx: {
                                                ...styles.headerCell,
                                                width: '20%',
                                                maxWidth: 45
                                            }, children: "Time" }), _jsx(TableCell, { sx: {
                                                ...styles.headerCell,
                                                width: '80%'
                                            }, children: activeDay.toLocaleDateString(undefined, {
                                                weekday: 'short',
                                                month: 'short',
                                                day: 'numeric'
                                            }) })] }) }), _jsx(TableBody, { children: HOURS.map((hour) => (_jsxs(TableRow, { children: [_jsx(TimeCell, { hour: hour, mobile: true }), _jsx(EventCell, { date: activeDay, hour: hour, items: items, handleOpenModal: handleOpenModal, mobile: true, isSponsorshipDetailsCalendarTab: isSponsorshipDetailsCalendarTab })] }, hour))) })] }) })] }));
    };
    const DesktopView = () => (_jsx(Box, { sx: { width: '100%', overflowX: 'auto' }, children: _jsxs(Table, { sx: {
                minWidth: 900,
                bgcolor: '#fff',
                '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
                    borderRight: '1px solid #f0f0f0'
                }
            }, children: [_jsx(TableHead, { children: _jsxs(TableRow, { style: { background: '#fafbfc' }, children: [_jsx(TableCell, { sx: {
                                    ...styles.headerCell,
                                    width: 80,
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 11
                                }, children: t('fields.time') }), days.map((d, idx) => (_jsx(TableCell, { sx: {
                                    ...styles.headerCell,
                                    py: 1.5,
                                    minWidth: 100,
                                    maxWidth: 130,
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 11
                                }, children: d.toLocaleDateString(undefined, {
                                    weekday: 'short',
                                    month: 'short',
                                    day: 'numeric'
                                }) }, idx)))] }) }), _jsx(TableBody, { children: HOURS.map((hour) => (_jsxs(TableRow, { children: [_jsx(TimeCell, { hour: hour }), days.map((d, idx) => (_jsx(EventCell, { date: d, hour: hour, items: items, handleOpenModal: handleOpenModal, isSponsorshipDetailsCalendarTab: isSponsorshipDetailsCalendarTab }, idx)))] }, hour))) })] }) }));
    return (_jsx(Box, { sx: {
            maxHeight: isMobile && isSponsorshipDetailsCalendarTab
                ? 'calc(100vh - 360px)'
                : isSponsorshipDetailsCalendarTab
                    ? 'calc(100vh - 350px)'
                    : 'calc(100vh - 370px)',
            overflowY: 'auto',
            bgcolor: '#fff',
            borderRadius: 3
        }, children: isMobile ? _jsx(MobileView, {}) : _jsx(DesktopView, {}) }));
};
